/**
 * Host adapters — let the BuilderCanvas edit any node whose content is a JSON
 * block layout (a "dashboard" host), addressed by bundle.
 *
 * The canvas edits a set of `blocs`; the host decides where they live and how
 * they persist. A dashboard host keeps all blocs as one JSON blob in the node's
 * `field_layout`, read and written through the generic decoupled API. The blocs
 * carry no per-bloc syncUrls, so each is editable through the page-state path
 * (`BuilderUnit.isEditable = hasSubmissionUrl || !exists`) with its DataSync
 * inert; the dashboard's single save (this module) is the sole persistence.
 *
 * Blob schema: an array of `{ id, type, data, layout: {x,y,w,h} }`.
 *
 * The canvas is an imperative single-state engine (one `page.blocs` store, one
 * Save button, one isDirty), so a dashboard host plugs into it with imperative
 * fetches rather than a parallel reactive-json store. Credentials and CSRF are
 * added globally by `fetchPatch` for every `/decoupled-api/` request.
 */

/** Parse the `field_layout` blob into the new schema; ignore any legacy shape. */
const parseBlocs = (layoutStr) => {
  let arr
  try { arr = JSON.parse(layoutStr || '[]') }
  catch { return [] }
  if (!Array.isArray(arr)) return []
  return arr
    // New schema entries carry `id` + `type`. Legacy widget entries
    // (`{ <type>: {...} }`) have neither → skipped (no back-compat).
    .filter(e => e && typeof e === 'object' && e.id && e.type)
    .map(e => ({
      id: String(e.id),
      nid: null,
      type: e.type,
      data: e.data ?? {},
      hiddenOn: [],
      layout: e.layout ?? {},
      // No per-bloc Syncable Object → editable via page-state, DataSync inert.
      syncUrls: null,
    }))
}

/** Serialize canvas blocs back into the `field_layout` blob schema. */
const serializeBlocs = (blocs) =>
  JSON.stringify((blocs ?? []).map(b => ({
    id: b.id,
    type: b.type,
    data: b.data ?? {},
    layout: b.layout ?? {},
  })))

/** Thrown when the current user may not view the dashboard (HTTP 403/404). */
export class DashboardAccessError extends Error {
  constructor(status) {
    super(`Dashboard access denied (HTTP ${status})`)
    this.name = 'DashboardAccessError'
    this.status = status
  }
}

/**
 * Load one dashboard (`host = { kind: bundle, id }`, where `id` is the portable
 * slug or the numeric node id) through the module's resolver
 * (`/api/orbit/dashboard/{slug}`) — a SINGLE access-checked item, unlike the
 * decoupled collection which leaks every dashboard's name/slug to anyone and
 * ships a reader-vs-writer shape. Works for the owner, shared users AND
 * anonymous viewers of a public dashboard; `submissionUrl` is null for readers.
 * `fresh` bypasses the in-browser cache — required after the chat agent writes
 * the layout in the sidecar, which cannot invalidate this tab's cached GET.
 * Throws `DashboardAccessError` on 403/404 so the canvas can show a clean notice.
 */
export const loadDashboard = async (host, { fresh = false } = {}) => {
  const { kind: bundle, id } = host
  const res = await fetch(`/api/orbit/dashboard/${encodeURIComponent(id)}`, fresh ? { cache: 'no-store' } : undefined)
  if (res.status === 403 || res.status === 404) throw new DashboardAccessError(res.status)
  if (!res.ok) throw new Error(`Dashboard load failed (HTTP ${res.status})`)
  const d = await res.json()
  return {
    title: d.name ?? '',
    blocs: parseBlocs(d.layout),
    hostMeta: {
      kind: bundle,
      id: d.id,
      slug: d.slug ?? '',
      name: d.name ?? '',
      ownerUid: d.ownerUid ?? '',
      // Editors only; readers/anonymous get null → the Save path stays inert.
      submissionUrl: d.submissionUrl ?? null,
      canEdit: !!d.canEdit,
      canManage: !!d.canManage,
    },
  }
}

/**
 * Persist the canvas blocs into the dashboard's `field_layout` (single POST via
 * the decoupled submission URL). Preserves `name`/`ownerUid`.
 */
export const saveDashboard = async (hostMeta, blocs) => {
  if (!hostMeta?.submissionUrl) throw new Error('Dashboard save: missing submissionUrl')
  // credentials + X-CSRF-Token added globally by fetchPatch for /decoupled-api/.
  const res = await fetch(hostMeta.submissionUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      // Omit ownerUid when empty so we never blank an existing owner reference.
      data: {
        name: hostMeta.name,
        ...(hostMeta.ownerUid !== '' && hostMeta.ownerUid != null ? { ownerUid: hostMeta.ownerUid } : {}),
        layout: serializeBlocs(blocs),
      },
    }),
  })
  if (!res.ok) throw new Error(`Dashboard save failed (HTTP ${res.status})`)
  return res.json().catch(() => null)
}
